import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input, Button } from '../../components/common';
import { BrowserMultiFormatReader, NotFoundException } from '@zxing/library';
import './LinkDevice.css';

export default function LinkDevice() {
  const navigate = useNavigate();
  const [serialNumber, setSerialNumber] = useState('');
  const [cameraError, setCameraError] = useState(null);
  const [scannedCode, setScannedCode] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [linkingDevice, setLinkingDevice] = useState(false);

  const videoRef = useRef(null);
  const readerRef = useRef(null);
  const scannerControlRef = useRef(null);
  const hasScannedRef = useRef(false);
  const isMountedRef = useRef(true);

  // ✅ COMPONENT MOUNT: create reader + start scanner
  useEffect(() => {
    isMountedRef.current = true;
    console.log('✅ LinkDevice mounted');

    readerRef.current = new BrowserMultiFormatReader();
    console.log('✅ ZXing reader created');

    startScanner();

    return () => {
      console.log('❌ LinkDevice unmounting');
      isMountedRef.current = false;
      stopScannerAndCleanup();
    };
  }, []);

  // ✅ HANDLE CAMERA ERRORS
  const handleCameraError = (err) => {
    console.error('❌ Camera error:', err);
    let errorMsg = 'Camera access error';

    if (err.name === 'NotAllowedError') {
      errorMsg = '❌ Camera permission denied. Please allow camera access in browser settings.';
    } else if (err.name === 'NotFoundError') {
      errorMsg = '❌ No camera found on this device.';
    } else if (err.name === 'NotReadableError') {
      errorMsg = '❌ Camera in use by another app. Close other camera apps.';
    } else if (err.name === 'SecurityError' || err.name === 'TypeError') {
      errorMsg = '❌ Camera requires HTTPS or localhost / 127.0.0.1';
    } else if (err.message) {
      errorMsg = `❌ ${err.message}`;
    }

    if (isMountedRef.current) {
      setCameraError(errorMsg);
      setIsScanning(false);
      setCameraReady(false);
    }
  };

  // ✅ START ZXING SCANNER (ZXing does getUserMedia itself)
  const startScanner = async () => {
    try {
      if (!videoRef.current) {
        console.error('❌ Video element not found');;
        return;
      }

      if (!readerRef.current) {
        readerRef.current = new BrowserMultiFormatReader();
      }

      console.log('🔍 Starting ZXing scanner with constraints...');

      setCameraError(null);
      setCameraReady(false);
      setIsScanning(true);

      const constraints = {
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      };

      // ZXing handles getUserMedia + attaches stream to videoRef.current
      const controls = await readerRef.current.decodeFromConstraints(
        constraints,
        videoRef.current,
        (result, err) => {
          if (result) {
            const code = result.getText();
            console.log('✅✅✅ QR CODE DETECTED:', code);

            if (!hasScannedRef.current && isMountedRef.current) {
              hasScannedRef.current = true;
              setScannedCode(code);
              setSerialNumber(code);

              // Auto submit disabled, just fill the field
              setTimeout(() => {
                if (isMountedRef.current) {
                  console.log('⏸️ Auto-submit disabled; waiting for user to press Link Device.');
                  // handleLinkDevice(code);
                }
              }, 600);
            }
          }

          // Ignore "no code found" errors; log others for debug
          if (err && !(err instanceof NotFoundException)) {
            // console.warn('Decode error:', err);
          }
        }
      );

      scannerControlRef.current = controls;

      if (isMountedRef.current) {
        console.log('✅ Scanner running. Waiting for QR codes…');
        setCameraReady(true);
        setIsScanning(true);
      }
    } catch (err) {
      console.error('❌ Scanner start failed:', err);
      handleCameraError(err);
    }
  };

  // ✅ STOP SCANNER AND CLEANUP
  const stopScannerAndCleanup = async () => {
    try {
      console.log('🛑 Stopping scanner...');

      if (scannerControlRef.current) {
        scannerControlRef.current.stop();
        scannerControlRef.current = null;
        console.log('✅ Scanner controls stopped');
      }

      // Extra safety: stop any remaining tracks on the video element
      const video = videoRef.current;
      if (video && video.srcObject) {
        const tracks = video.srcObject.getTracks();
        tracks.forEach((t) => t.stop());
        video.srcObject = null;
        console.log('✅ Video tracks stopped');
      }

      if (isMountedRef.current) {
        setIsScanning(false);
        setCameraReady(false);
      }
    } catch (err) {
      console.error('Stop error:', err);
    }
  };

  // ✅ LINK DEVICE
  const handleLinkDevice = async (value) => {
    const finalValue = value || serialNumber;

    if (!finalValue.trim()) {
      alert('❌ Please enter a valid serial number');
      if (isMountedRef.current) {
        hasScannedRef.current = false;
      }
      return;
    }

    console.log('🔗 LINKING DEVICE:', finalValue);

    if (!isMountedRef.current) return;
    setLinkingDevice(true);

    try {
      // Stop scanner before linking
      await stopScannerAndCleanup();

      // Get token
      const token =
        localStorage.getItem('authToken') ||
        localStorage.getItem('token') ||
        localStorage.getItem('jwt_token');

      if (!token) {
        console.error('❌ No auth token found');
        if (isMountedRef.current) {
          alert('❌ Not authenticated. Please login again.');
          navigate('/login');
        }
        return;
      }

      const apiUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
      const endpoint = `${apiUrl}/api/v1/user/linkdevice`;

      console.log('📡 Sending API request to:', endpoint);

      const response = await fetch(endpoint, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
              body: JSON.stringify({
        deviceId: finalValue.toUpperCase()
      }),

      });

      // const data = await response.json();
      // console.log('✅ API Response:', data);

      if (response.ok) {
        if (isMountedRef.current) {
          alert('✅ Device linked successfully!');
          navigate('/home');
        }
      } else {
        const errorMsg = data.error || data.message || 'Failed to link device';
        console.error('❌ API Error:', errorMsg);

        if (isMountedRef.current) {
          alert(`❌ ${errorMsg}`);

          // Reset for retry
          hasScannedRef.current = false;
          setScannedCode(null);
          setSerialNumber('');
          setLinkingDevice(false);

          // Restart scanner
          setTimeout(() => {
            if (isMountedRef.current) {
              console.log('🔄 Restarting scanner for retry...');
              startScanner();
            }
          }, 500);
        }
      }
    } catch (err) {
      console.error('❌ Link device error:', err);
      if (isMountedRef.current) {
        alert(`❌ Error: ${err.message}`);

        hasScannedRef.current = false;
        setScannedCode(null);
        setSerialNumber('');
        setLinkingDevice(false);

        setTimeout(() => {
          if (isMountedRef.current) {
            console.log('🔄 Restarting scanner after error...');
            startScanner();
          }
        }, 500);
      }
    } finally {
      if (isMountedRef.current) {
        setLinkingDevice(false);
      }
    }
  };

  // ✅ MANUAL LINK
  const handleManualLink = async () => {
    if (!serialNumber.trim()) {
      alert('❌ Please enter a serial number');
      return;
    }

    console.log('📝 Manual link initiated');
    await stopScannerAndCleanup();
    handleLinkDevice();
  };

  // ✅ RESCAN
  const handleRescan = async () => {
    console.log('🔄 Rescanning...');

    if (!isMountedRef.current) return;

    hasScannedRef.current = false;
    setScannedCode(null);
    setSerialNumber('');
    setCameraError(null);
    setLinkingDevice(false);

    await stopScannerAndCleanup();

    setTimeout(() => {
      if (isMountedRef.current) {
        console.log('🔄 Starting new scan...');
        startScanner();
      }
    }, 300);
  };

  // ✅ BACK BUTTON
  const handleBack = async () => {
    console.log('⬅️ Going back to home...');
    isMountedRef.current = false;
    await stopScannerAndCleanup();
    navigate('/home');
  };

  return (
    <div className="link-device-root">
      {/* HEADER */}
      <div className="link-device-header">
        <div className="link-device-header-side">
          <button
            className="link-device-back-btn"
            onClick={handleBack}
            aria-label="Back"
            title="Go back to home"
          >
            <i className="fas fa-arrow-left"></i>
          </button>
        </div>
        <div className="link-device-header-center">
          <h1 className="link-device-title">Link Device</h1>
        </div>
        <div className="link-device-header-side"></div>
      </div>

      {/* BODY */}
      <div className="link-device-container">
        <p className="link-device-instruction">
          {linkingDevice
            ? ' Linking device...'
            : isScanning && !scannedCode && !cameraError
            ? ' Point camera at QR code'
            : !cameraReady && !cameraError
            ? 'Initializing camera...'
            : cameraError
            ? 'Camera unavailable. You can still link your device manually below.'
            : ''}
        </p>

        {cameraError && (
          <div className="camera-error-message">
            <i className="fas fa-exclamation-circle"></i>
            <span>{cameraError}</span>
          </div>
        )}

        {scannedCode && (
          <div className="scanned-success">
            <i className="fas fa-check-circle"></i>
            <span>Scanned: {scannedCode}</span>
          </div>
        )}

        {/* Only hide camera when error; manual UI is independent */}
        {!cameraError && (
          <video
            ref={videoRef}
            className="qr-scanner-video"
            autoPlay
            playsInline
            muted
          />
        )}

        {/* Divider is ALWAYS visible */}
        <div className="link-device-divider">
          <span>----- OR -----</span>
        </div>

        {/* Manual section ALWAYS visible */}
        <div className="link-device-manual-section">
          <p className="link-device-manual-label">
            Enter device serial<br />number manually
          </p>
          <Input
            type="text"
            placeholder="Enter Serial Number"
            className="link-device-input"
            value={serialNumber}
            onChange={(e) => setSerialNumber(e.target.value.toUpperCase())}
            disabled={linkingDevice}
            required
          />
        </div>

        {/* Button group ALWAYS visible */}
        <div className="link-device-button-group">
          {scannedCode && !cameraError ? (
            <>
              <Button
                onClick={handleRescan}
                className="link-device-btn-secondary"
                disabled={linkingDevice}
              >
                Scan Again
              </Button>
              <Button
                onClick={() => handleLinkDevice()}
                className="link-device-btn"
                disabled={linkingDevice}
              >
                {linkingDevice ? '⏳ Linking...' : 'Link Device'}
              </Button>
            </>
          ) : (
            <Button
              onClick={handleManualLink}
              className="link-device-btn"
              disabled={!serialNumber.trim() || linkingDevice}
            >
              {linkingDevice ? '⏳ Linking...' : 'Link Device'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
